import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { EmployeeData, VehicleData } from "../../../models/monitorView/monitorViewTypes";
import VehicleFilters from "./filters/vehicleFilters";
import VehicleGroupFilter from "./filters/vehicleGroupFilter";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { setSelectedVehicleGroupIds } from "../../../store/monitorViewFiltersSlice";
import { Grid } from "@mui/material";
import { MonitorViewWorktimeFilters } from "./filters/monitorViewWorktimeFilters";
import { handleVehicleSelect } from "../monitorViewUtils";
import { setSelectedEmployeeId, toggleWorkShiftView } from "../../../store/monitorViewSlice";
import { getEmployeeGroups } from "../../../services/employeeGroupService";
import { useFetch } from "../../../hooks/useFetch";
import { employeeGroupListItemDto } from "../../../models/employeeGroup/employeeGroup";
import FleetOverviewCard from "./fleetOverviewCard/fleetOverviewCard";

interface MonitorViewOverviewSidebarProps {
    vehicleData: VehicleData[];
    employeeData: EmployeeData[];
}

export const MonitorViewOverviewSidebar = ({
    vehicleData,
    employeeData
}: MonitorViewOverviewSidebarProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { selectedVehicleGroupIds } = useAppSelector((state) => state.monitorViewFilters);
    const { selectedVehicleId, isVehicleDetailView, selectedEmployeeId } = useAppSelector((state) => state.monitorView);
    const [employeeGroups, setEmployeeGroups] = useState<employeeGroupListItemDto[]>();
    const positions = useAppSelector((state) => state.monitorView.positions);
    const employees = useAppSelector((state) => state.employees.employees);

    const countByWorkTimeType = useMemo(() => {
        const counts = employees?.reduce((acc, e) => {
            const position = positions?.find((p) => p.employeeWorkTimeTypes.some((w) => w.employeeId === e.id));
            const workTimeTypeId = position?.employeeWorkTimeTypes[0]?.workTimeTypeId;
            if (!position || workTimeTypeId === null) {
                acc["noWork"] = (acc["noWork"] || 0) + 1;
            } else {
                acc[workTimeTypeId] = (acc[workTimeTypeId] || 0) + 1;
                acc["work"] = (acc["work"] || 0) + 1;
            }
            return acc;
        }, {} as { [key: string]: number }) || {};
        return counts;
    }, [positions, employees]);

    const countByDrivingStatus = useMemo(() => {
        const counts = positions?.reduce((acc, p) => {
            const drivingStatus = p.driving;
            acc[drivingStatus] = (acc[drivingStatus] || 0) + 1;
            return acc;
        }, {} as { [key: number]: number }) || {};
        return counts;
    }, [positions]);

    const onSelectVehicleId = (vehicleId: string) => {
        handleVehicleSelect(vehicleId,
            vehicleData,
            isVehicleDetailView,
            dispatch);
    };

    const onSelectEmployeeId = (employeeId: string) => {
        dispatch(setSelectedEmployeeId(employeeId));
        dispatch(toggleWorkShiftView());
    };

    useFetch(
        (signal) => getEmployeeGroups(true, signal),
        {
            // This could be moved to state.
            onSuccess: (res) =>
                setEmployeeGroups(
                    res.data.filter((d) => !!d.salaryPeriodTypeId)
                ),
        },
        []
    );

    const employeesWithoutVehicles = useMemo(() => {
        // Collect all employeeIds that are assigned to a visible vehicle
        const assignedEmployeeIds = new Set(
            vehicleData?.map(v => v.employeeId)
        );

        // Return employees NOT in any vehicles
        return employeeData?.filter(
            employee => !assignedEmployeeIds.has(employee.employeeId)
        );
    }, [employeeData, vehicleData]);

    const renderCards = (
        <>
            {vehicleData?.map((v) => (
                <FleetOverviewCard
                    key={`vehicle-${v.vehicleId}-employee-${v.employeeId ?? "none"}`}
                    vehicle={v}
                    employee={employeeData.find((e) => e.employeeId === v.employeeId)}
                    employeeGroups={employeeGroups}
                    isSelected={v.vehicleId === selectedVehicleId}
                    onClickCard={() => {
                        onSelectVehicleId(v.vehicleId);
                    }}
                    onSelectEmployee={() => {
                        onSelectEmployeeId(v.employeeId);
                    }}
                />
            ))}

            {employeesWithoutVehicles?.map((e) => (
                <FleetOverviewCard
                    key={`vehicle-novehicle-employee-${e.employeeId}`}
                    vehicle={null}
                    employee={e}
                    employeeGroups={employeeGroups}
                    isSelected={e.employeeId === selectedEmployeeId}
                    onClickCard={() => {
                        if (e.currentWorkShift) {
                            // At the moment if workshift is not running for employee, employee is inactive
                            onSelectEmployeeId(e.employeeId);
                        }
                    }}
                    onSelectEmployee={() => {
                        if (e.currentWorkShift) {
                            // At the moment if workshift is not running for employee, employee is inactive
                            onSelectEmployeeId(e.employeeId);
                        }
                    }}
                />
            ))}
        </>
    );

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <VehicleFilters countByDrivingStatus={countByDrivingStatus} />
                </Grid>
                <Grid item xs={12}>
                    <MonitorViewWorktimeFilters countByWorkTimeType={countByWorkTimeType} />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <VehicleGroupFilter selectedGroupIds={selectedVehicleGroupIds} setSelectedVehicleGroupIds={setSelectedVehicleGroupIds} />
                </Grid>
            </Grid >
            {vehicleData?.length > 0 || employeesWithoutVehicles.length > 0 ? (
                renderCards
            ) : (
                <Alert variant="standard" severity="info">
                    {t("monitorView.sidebar.overview.noRows")}
                </Alert>
            )}
        </>
    );
};
